import { JitsiConference, JitsiMeet, JitsiParticipant } from "lib/lib-jitsi-meet";
import { action } from "mobx";
import { DataMessage, PrivateChatMessage } from "../../chat";
import { ChatRoomState, getDefaultChatUIState, getToaster, RoomState } from "../../common";
import AbstractDataComponent from "./AbstractDataComponent";

export interface PrivateMessageManagerProps {
    jitsi: JitsiMeet;
    room: JitsiConference;
    roomState: RoomState;
    chats: ChatRoomState["private"];
}

export default class extends AbstractDataComponent<PrivateMessageManagerProps> {
    private toastId?: string;

    public componentDidMount() {
        const { jitsi, room } = this.props;
        room.on(jitsi.events.conference.ENDPOINT_MESSAGE_RECEIVED, this.handleDataMessageReceived);
    }

    public componentWillUnmount() {
        const { jitsi, room } = this.props;
        room.off(jitsi.events.conference.ENDPOINT_MESSAGE_RECEIVED, this.handleDataMessageReceived);
    }

    @action
    private handleMessageReceived = (participant: JitsiParticipant, message: string) => {
        const { chats, roomState } = this.props;
        const userId = participant.getId();
        const newMessage = {
            userId,
            message,
            timestamp: new Date(),
            userNickname: participant.getDisplayName(),
            isSelf: false,
        };

        if (chats.has(userId)) {
            const chat = chats.get(userId)!;
            chat.messages.push(newMessage);

            const layout = roomState.videoLayout.find(({ userId: id }) => id === userId)!;
            if (!layout.isDropdownOpen && this.toastId === undefined) {
                this.toastId = getToaster().show({
                    message: `New message from ${participant.getDisplayName()}`,
                    action: {
                        text: "Show chat",
                        onClick: () => {
                            layout.isDropdownOpen = true;
                            this.handleNotificationDismiss();
                        },
                    },
                    onDismiss: this.handleNotificationDismiss,
                });
            }
        } else {
            // TODO: shouldn't need this line, unreachable state
            chats.set(userId, getDefaultChatUIState([newMessage]));
        }
    };

    @action
    private handleDataMessageReceived = (participant: JitsiParticipant, message: DataMessage) => {
        const { privateChatRequests } = this.props.roomState;
        const userId = participant.getId();

        switch (message.kind) {
            case "chatRequest":
                privateChatRequests.set(userId, "incomingRequest");
                break;
            case "chatAccept":
                privateChatRequests.set(userId, "incomingAccept");
                break;
            case "chatBlock":
                privateChatRequests.set(userId, "incomingBlock");
                break;
            case "chatPrivateMessage":
                const { to, message: msg } = (message as any) as PrivateChatMessage;
                // TODO: guard is unnecessary
                if (to === this.props.room.myUserId()) {
                    this.handleMessageReceived(participant, msg);
                }
        }
    };

    @action
    private handleNotificationDismiss = () => {
        if (this.toastId !== undefined) {
            getToaster().dismiss(this.toastId);
            this.toastId = undefined;
        }
    };
}
